import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { RefreshCw, ShieldCheck } from "lucide-react";
import { api, isPurchasePausedError, isQuoteRefreshError, postJSON } from "../api";
import { QuoteRefreshNotice } from './QuoteRefreshNotice';
import { PurchasePauseNotice } from './PurchasePauseNotice';
import { useApp } from "../app-context";
import { money } from "../format";
import { countries } from "../countries";
import type { ForwardingOrder, RateOption } from "../types";

// Keep the provider SDK off public pages and out of the application payment rules.
const CardFields = lazy(() =>
  import("@nmipayments/nmi-pay-react").then((module) => ({
    default: memo(module.NmiPayments),
  })),
);
const cardMethods: Array<"card"> = ["card"];
const cardAppearance = { theme: "light" as const };

export type PaymentState =
  "none" | "pending" | "unknown" | "failed" | "succeeded" | "refunded";
export type PaymentResponse = {
  payment: { id?: string; state: PaymentState; message?: string };
  order?: ForwardingOrder | null;
};
type Billing = {
  first_name: string;
  last_name: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};

export function PaymentCheckout({
  order,
  rate,
  onAuthorized,
  onBusyChange,
}: {
  order: ForwardingOrder;
  rate: RateOption;
  onAuthorized: (result: { order: ForwardingOrder }) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  return (
    <SecureCardCheckout
      endpoint={`/api/orders/${order.id}/payment`}
      rate={rate}
      description="Pay for package forwarding, not the products."
      onPaid={(result) => {
        if (result.order?.rxid) onAuthorized({ order: result.order });
      }}
      onBusyChange={onBusyChange}
    />
  );
}

// The payment transport and secure fields are shared; each shipment keeps its own endpoint and state.
export function SecureCardCheckout({
  endpoint,
  rate,
  description,
  onPaid,
  onBusyChange,
}: {
  endpoint: string;
  rate: RateOption;
  description: string;
  onPaid: (result: PaymentResponse) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { config } = useApp();
  const [payment, setPayment] = useState<PaymentResponse["payment"]>({
    state: "none",
  });
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [serverPaused, setServerPaused] = useState(false);
  const [quoteExpired, setQuoteExpired] = useState(false);
  const paused = config.purchasesPaused || serverPaused;
  const [billing, setBilling] = useState<Billing>({
    first_name: "",
    last_name: "",
    address1: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });
  const billingRef = useRef(billing);
  useEffect(() => {
    billingRef.current = billing;
  }, [billing]);
  const requestId = useRef(crypto.randomUUID());
  const submitting = useRef(false);
  const paymentBlocked = useRef(true);
  const form = useRef<HTMLFormElement>(null);
  const authorized = useRef(onPaid);
  useEffect(() => {
    authorized.current = onPaid;
  }, [onPaid]);
  const needsRecovery = payment.state !== "none" && payment.state !== "failed";
  const busy = loading || processing || needsRecovery || paused || quoteExpired;
  useEffect(() => {
    onBusyChange(busy);
  }, [busy, onBusyChange]);

  const apply = useCallback((result: PaymentResponse) => {
    paymentBlocked.current =
      result.payment.state !== "none" && result.payment.state !== "failed";
    setPayment(result.payment);
    if (result.payment.state === "succeeded") authorized.current(result);
    if (result.payment.state === "failed")
      requestId.current = crypto.randomUUID();
  }, []);

  useEffect(() => {
    let active = true;
    api<PaymentResponse>(endpoint)
      .then((result) => {
        if (active) {
          apply(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active)
          setError(
            "Could not load payment status. Refresh this page before paying.",
          );
      });
    return () => {
      active = false;
    };
  }, [endpoint, apply]);

  async function checkStatus() {
    setChecking(true);
    try {
      apply(await postJSON<PaymentResponse>(`${endpoint}/reconcile`, {}));
      setError("");
    } catch {
      setError(
        "Could not confirm payment status. Please try checking again; do not make another payment.",
      );
    } finally {
      setChecking(false);
    }
  }

  const pay = useCallback(
    async (token: string): Promise<true | string> => {
      if (paused) return "New purchases are paused. Please contact support@rxrouting.com.";
      if (submitting.current || paymentBlocked.current)
        return "A payment is already being checked.";
      if (!config.payment.mock && !form.current?.reportValidity())
        return "Complete your billing details first.";
      submitting.current = true;
      setError("");
      // A local request in flight is not an uncertain provider outcome. Keep the
      // mounted secure fields (and the SDK's own processing button) in place.
      setProcessing(true);
      const testBilling = {
        first_name: "Test",
        last_name: "Customer",
        address1: "888 Test St",
        city: "New York",
        state: "NY",
        zip: "77777",
        country: "US",
      };
      try {
        const result = await postJSON<PaymentResponse>(endpoint, {
          requestId: requestId.current,
          paymentToken: token,
          billing: config.payment.mock ? testBilling : billingRef.current,
        });
        apply(result);
        return result.payment.state === "succeeded"
          ? true
          : result.payment.message || "Your payment is being confirmed.";
      } catch (error) {
        if (isQuoteRefreshError(error)) {
          // Confirmed rejection before a sale, not an unknown payment outcome.
          setQuoteExpired(true);
          return error.message;
        }
        if (isPurchasePausedError(error)) {
          // The server refused this request before any payment was attempted.
          setServerPaused(true);
          return error.message;
        }
        // The browser cannot know whether an interrupted request charged the card.
        // Only a status read can make another attempt available.
        paymentBlocked.current = true;
        setPayment({
          state: "unknown",
          message: "We are confirming your payment. Do not pay again.",
        });
        return "We are confirming your payment. Do not pay again.";
      } finally {
        submitting.current = false;
        setProcessing(false);
      }
    },
    [config.payment.mock, endpoint, apply, paused],
  );
  const onToken = useCallback(
    ({ token }: { token: string }) => pay(token),
    [pay],
  );

  if (loading)
    return (
      <div role="status" className="card-checkout__status">
        {error || "Checking payment status…"}
      </div>
    );
  if (!processing && needsRecovery)
    return (
      <div className="card-checkout__status" role="status">
        <h3>
          {payment.state === "refunded"
            ? "Payment reversed"
            : "Confirming your payment"}
        </h3>
        <p>
          {payment.message ||
            "We are checking the original payment. Please do not submit another charge."}
        </p>
        {error && <p role="alert">{error}</p>}
        <button
          className="button button--outline"
          disabled={checking}
          onClick={checkStatus}
        >
          <RefreshCw size={16} />
          {checking ? "Checking…" : "Check payment status"}
        </button>
      </div>
    );
  if (quoteExpired && !processing) return <QuoteRefreshNotice />;
  if (paused && !processing) return <PurchasePauseNotice />;
  if (!config.payment.configured && !config.payment.mock)
    return (
      <p className="card-checkout__status">
        Card checkout is not available yet. Please contact
        support@rxrouting.com.
      </p>
    );

  return (
    <div className="card-checkout">
      {config.payment.environment !== "live" && !config.payment.mock && (
        <p className="warning-box" role="note">
          <b>Test checkout.</b> Use test card details only. No real payment or
          shipment will be made.
        </p>
      )}
      <div className="payment-panel__head">
        <ShieldCheck />
        <div>
          <b>Secure card checkout</b>
          <span>{description}</span>
        </div>
      </div>
      {!processing && payment.message && (
        <p className="form-error" role="alert">
          {payment.message}
        </p>
      )}
      <div inert={processing} aria-busy={processing}>
        {config.payment.mock ? (
          <div className="card-checkout__test">
            <span className="mono-label">LOCAL PAYMENT SIMULATOR</span>
            <p>
              No card details or real money. These buttons exercise the full
              order flow.
            </p>
            <button
              className="button button--primary"
              disabled={processing}
              onClick={() => pay("test-approved")}
            >
              {processing
                ? "Processing payment…"
                : `Simulate payment of ${money(rate.price.totalMinor, rate.price.currency)}`}
            </button>
            <button
              className="button button--outline"
              disabled={processing}
              onClick={() => pay("test-declined")}
            >
              Simulate declined card
            </button>
            <button
              className="button button--outline"
              disabled={processing}
              onClick={() => pay("test-timeout")}
            >
              Simulate lost payment response
            </button>
          </div>
        ) : (
          <form ref={form} onSubmit={(event) => event.preventDefault()}>
            <h3>Billing details</h3>
            <p className="card-checkout__hint">
              Use the name and address on your card. This can differ from your
              shipping destination.
            </p>
            <div className="form-grid">
              {(
                [
                  ["first_name", "First name", "given-name"],
                  ["last_name", "Last name", "family-name"],
                  ["address1", "Billing street address", "address-line1"],
                  ["city", "City", "address-level2"],
                  ["state", "State / region (optional)", "address-level1"],
                  ["zip", "Postal code (if applicable)", "postal-code"],
                ] as const
              ).map(([key, label, autoComplete]) => (
                <label
                  key={key}
                  className={key === "address1" ? "span-2" : undefined}
                >
                  {label}
                  <input
                    autoComplete={autoComplete}
                    required={key !== "state" && key !== "zip"}
                    maxLength={key === "zip" ? 20 : 100}
                    value={billing[key]}
                    onChange={(event) =>
                      setBilling({ ...billing, [key]: event.target.value })
                    }
                  />
                </label>
              ))}
              <label>
                Billing country
                <select
                  autoComplete="country"
                  required
                  value={billing.country}
                  onChange={(event) =>
                    setBilling({ ...billing, country: event.target.value })
                  }
                >
                  <option value="">Select country</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Suspense
              fallback={<p role="status">Loading secure card fields…</p>}
            >
              <CardFields
                tokenizationKey={config.payment.tokenizationKey}
                layout="multiLine"
                paymentMethods={cardMethods}
                appearance={cardAppearance}
                payButtonText={`Pay ${money(rate.price.totalMinor, rate.price.currency)}`}
                onPay={onToken}
              />
            </Suspense>
          </form>
        )}
      </div>
      <small className="secure-copy" role="status" aria-live="polite">
        {processing
          ? "Processing your payment. Please keep this page open."
          : "Card details go directly to our payment gateway. RX Routing does not store your card number."}
      </small>
    </div>
  );
}
